import { useEffect, useState } from 'react';
import { ErrorBanner, Spinner } from '@/components/ui';
import { portfolioCodeQualityApi } from '@/services/portfolio-code-quality-api';
import type { IMetricViewFilters } from '@/hooks/use-metric-view-filters';
import type { ITeamQualityRow } from '@skystone/shared';
import { CoverageByTeamPanel } from './CoverageByTeamPanel';
import { QgPassRateByTeamPanel } from './QgPassRateByTeamPanel';

interface TeamBreakoutsSlotProps {
  filters: IMetricViewFilters;
}

/**
 * Team-breakouts slot — two side-by-side panels (Coverage % by Team, Quality
 * Gate Pass Rate by Team). Hidden by `CodeQualityShell` when `filters.teamId`
 * is set; this component is therefore only rendered at All-Teams scope.
 *
 * Fetches `code-quality-by-team` on mount + scope change. Both panels read
 * from the same `teams` array but apply different sort/filter rules
 * (`team-row-formatter`).
 */
export function TeamBreakoutsSlot({ filters }: Readonly<TeamBreakoutsSlotProps>) {
  const [teams, setTeams] = useState<ITeamQualityRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setTeams(null);
    setError(null);

    portfolioCodeQualityApi
      .getByTeam({
        from: filters.from,
        to: filters.to,
        productId: filters.productId || undefined,
      })
      .then((response) => {
        if (cancelled) return;
        setTeams(response.teams);
      })
      .catch(() => {
        if (cancelled) return;
        setError('Failed to load team breakouts.');
        setTeams([]);
      });

    return () => {
      cancelled = true;
    };
  }, [filters.from, filters.to, filters.productId]);

  if (teams === null) {
    return (
      <div className="flex items-center justify-center py-6" data-testid="code-quality-team-breakouts">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="code-quality-team-breakouts">
      {error && <ErrorBanner message={error} />}
      <div className="grid gap-3 lg:grid-cols-2">
        <CoverageByTeamPanel teams={teams} />
        <QgPassRateByTeamPanel teams={teams} />
      </div>
    </div>
  );
}
